<template>
  <div
    v-if="formattedBadges.length"
    class="product-badges"
  >
    <div
      v-for="(badge, index) of formattedBadges.slice(0, 1)"
      :key="`${badge.handle}-${index}`"
      class="product-badges__badge"
      :class="badgeTextClasses"
      :style="{
        backgroundColor: badge.colour.background,
        color: badge.colour.text,
      }"
      v-text="badge.text"
    />

    <div
      v-if="loading"
      class="product-badges__badge"
      :class="badgeTextClasses"
      v-text="$string('accessibility.loading')"
    />
  </div>
</template>

<script>

/**
 * Vue: Product badges (product-badges)
 * -----------------------------------------------------------------------------
 * Tag-based product badges displayed in product card and PDP.
 *
 * @param {Object} product - Product.
 * @param {Array} badges - Product badge tags.
 * @param {Boolean} [large] - Large badges (PDP).
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean} [tiny] - Use smaller font sizes.
 *
 */
import { convertToHandle } from '~/helpers/convert'

export default {
  name: 'ProductBadges',

  props: {
    badges: {
      type: Array,
      default: () => [],
    },
    product: {
      type: Object,
      default: () => ({}),
    },
    large: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    tiny: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Compute badge text classes.
     * @returns {String}
     */
    badgeTextClasses() {
      if (this.large) {
        return 'product-badges__badge--large text-label-s'
      }

      return this.tiny
        ? 'text-label-xs'
        : 'text-label-xs text-label-s-desktop'
    },

    /**
     * Returns discount percentage.
     * @returns {Boolean|Number}
     */
    discountPercentage() {
      const discountBadge = cnvs.badges['discount-badge']
      const hasNoDiscountTag = discountBadge.tag === ''
        ? false
        : this.product?.tags?.some(tag => tag === discountBadge.tag)

      if (hasNoDiscountTag) {
        return false
      }

      const compareAtPrice = this.product?.compareAtPrice?.amount
      const price = this.product?.price?.amount

      if (!compareAtPrice && price < compareAtPrice) {
        return false
      }

      return compareAtPrice && price < compareAtPrice
        ? Math.floor(((compareAtPrice - price) / compareAtPrice) * 100)
        : false
    },

    /**
     * Find matching badge object based on tag.
     * - Filters out those without text values.
     * - Sorts based on priority.
     * @return {Array{}}
     */
    formattedBadges() {
      const discountBadge = cnvs.badges['discount-badge']

      if (this.discountPercentage && discountBadge.enable) {
        return [
          {
            colour: discountBadge.colour,
            text: discountBadge.text.replace('[x]', this.discountPercentage),
          },
        ]
      }

      return this.badges
        .map((badge) => {
          const sanitisedBadge = convertToHandle(badge)
          return cnvs.badges[sanitisedBadge]
        })
        .filter(Boolean)
        .filter((badge) => badge.text)
        .sort((a, b) => {
          return a.priority - b.priority
        })
    },
  },
}
</script>

<style lang="scss">
@import './product-badges';
</style>
